<script>
  import { onMount } from 'svelte';

  const API_BASE_URL = '/studentplayground';

  // State
  let sections = [];
  let loading = false;
  let errorMessage = '';
  let expandedSections = []; // Track which sections are expanded (array of IDs)
  let filterType = 'all'; // 'all', 'published', 'featured'
  let searchQuery = '';
  let sortBy = 'display_order'; // 'display_order', 'created_at', 'topic'

  // Fetch all sections
  async function fetchSections() {
    loading = true;
    errorMessage = '';
    const token = localStorage.getItem('jwtToken');
    
    if (!token) {
      errorMessage = 'JWT token not found. Please log in.';
      loading = false;
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/information-sections/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        sections = data.sections || [];
      } else {
        const errorData = await response.json().catch(() => ({}));
        errorMessage = errorData.error || `Failed to fetch sections: ${response.status}`;
      }
    } catch (error) {
      errorMessage = `Network error: ${error.message}`;
    } finally {
      loading = false;
    }
  }

  // Toggle section expansion
  function toggleSection(sectionId) {
    const index = expandedSections.indexOf(sectionId);
    if (index > -1) {
      // Remove from array
      expandedSections = [
        ...expandedSections.slice(0, index),
        ...expandedSections.slice(index + 1)
      ];
    } else {
      // Add to array
      expandedSections = [...expandedSections, sectionId];
    }
  }

  // Check if section is expanded (reactive)
  function isExpanded(sectionId) {
    return expandedSections.includes(sectionId);
  }

  // Filter sections based on filter type and search query
  $: filteredSections = sections.filter(section => {
    // Filter by type
    if (filterType === 'published' && !section.is_published) {
      return false;
    }
    if (filterType === 'featured' && !section.is_featured) {
      return false;
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return section.topic.toLowerCase().includes(query) ||
             (section.content && section.content.toLowerCase().includes(query));
    }
    
    return true;
  });

  // Sort sections
  $: sortedSections = [...filteredSections].sort((a, b) => {
    if (sortBy === 'display_order') {
      if (a.display_order !== b.display_order) {
        return a.display_order - b.display_order;
      }
      // Secondary sort by topic if display_order is same
      return a.topic.localeCompare(b.topic);
    } else if (sortBy === 'created_at') {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateB - dateA; // Newest first
    } else if (sortBy === 'topic') {
      return a.topic.localeCompare(b.topic);
    }
    return 0;
  });

  // Format date for display
  function formatDate(dateString) {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  }

  // Initialize on mount
  onMount(() => {
    fetchSections();
  });
</script>

<div class="information-sections-display">
  <div class="header">
    <h2>Information Sections</h2>
    <p class="description">View and browse all information sections with their full content.</p>
  </div>

  <!-- Controls -->
  <div class="controls">
    <div class="filter-group">
      <label for="filter-type">Filter:</label>
      <select id="filter-type" bind:value={filterType}>
        <option value="all">All Sections</option>
        <option value="published">Published Only</option>
        <option value="featured">Featured Only</option>
      </select>
    </div>

    <div class="search-group">
      <label for="search">Search:</label>
      <input
        id="search"
        type="text"
        bind:value={searchQuery}
        placeholder="Search by topic or content..."
      />
    </div>

    <div class="sort-group">
      <label for="sort">Sort by:</label>
      <select id="sort" bind:value={sortBy}>
        <option value="display_order">Display Order</option>
        <option value="created_at">Date Created</option>
        <option value="topic">Topic (A-Z)</option>
      </select>
    </div>

    <button on:click={fetchSections} class="btn-refresh" disabled={loading}>
      {loading ? 'Loading...' : 'Refresh'}
    </button>
  </div>

  <!-- Error Message -->
  {#if errorMessage}
    <div class="message error">{errorMessage}</div>
  {/if}

  <!-- Loading State -->
  {#if loading && sections.length === 0}
    <div class="loading">Loading sections...</div>
  {:else if sortedSections.length === 0}
    <div class="empty">
      {#if searchQuery || filterType !== 'all'}
        No sections match your filters. Try adjusting your search or filter criteria.
      {:else}
        No sections found. Create your first section in the "Information Sections" tab.
      {/if}
    </div>
  {:else}
    <!-- Sections List -->
    <div class="sections-list">
      {#each sortedSections as section (section.id)}
        {@const isExpandedSection = expandedSections.includes(section.id)}
        <div class="section-card" class:expanded={isExpandedSection}>
          <!-- Section Header (Always Visible) -->
          <div class="section-header" on:click={() => toggleSection(section.id)}>
            <div class="section-header-content">
              <div class="section-title-group">
                <h3 class="section-topic">{section.topic}</h3>
                <div class="badges">
                  {#if section.is_featured}
                    <span class="badge featured">Featured</span>
                  {/if}
                  {#if section.is_published}
                    <span class="badge published">Published</span>
                  {:else}
                    <span class="badge draft">Draft</span>
                  {/if}
                </div>
              </div>
              <div class="section-meta">
                <span class="meta-item">Order: {section.display_order}</span>
                <span class="meta-item">Views: {section.view_count || 0}</span>
                <span class="meta-item">Created: {formatDate(section.created_at)}</span>
              </div>
            </div>
            <div class="expand-icon" class:expanded={isExpandedSection}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>

          <!-- Section Content (Expanded) -->
          {#if isExpandedSection}
            <div class="section-content">
              <div class="content-wrapper">
                {@html section.content}
              </div>
              {#if section.created_by || section.last_updated_by}
                <div class="section-footer">
                  {#if section.created_by}
                    <span class="footer-item">Created by: {section.created_by}</span>
                  {/if}
                  {#if section.last_updated_by}
                    <span class="footer-item">Last updated by: {section.last_updated_by}</span>
                  {/if}
                  {#if section.published_at}
                    <span class="footer-item">Published: {formatDate(section.published_at)}</span>
                  {/if}
                </div>
              {/if}
            </div>
          {/if}
        </div>
      {/each}
    </div>
  {/if}
</div>

<style>
  .information-sections-display {
    max-width: 100%;
    padding: 20px;
  }

  .header h2 {
    color: #fab45a;
    margin-bottom: 10px;
  }

  .description {
    color: #666;
    margin-bottom: 25px;
    font-size: 1em;
  }

  .controls {
    display: flex;
    gap: 15px;
    margin-bottom: 25px;
    flex-wrap: wrap;
    align-items: flex-end;
    padding: 15px;
    background: #f8f9fa;
    border-radius: 8px;
  }

  .filter-group,
  .search-group,
  .sort-group {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .filter-group label,
  .search-group label,
  .sort-group label {
    font-size: 12px;
    font-weight: 500;
    color: #555;
  }

  .filter-group select,
  .sort-group select {
    padding: 8px 12px;
    border: 2px solid #ddd;
    border-radius: 6px;
    font-size: 14px;
    background: white;
    cursor: pointer;
  }

  .filter-group select:focus,
  .sort-group select:focus {
    outline: none;
    border-color: #fab45a;
  }

  .search-group input {
    padding: 8px 12px;
    border: 2px solid #ddd;
    border-radius: 6px;
    font-size: 14px;
    min-width: 200px;
  }

  .search-group input:focus {
    outline: none;
    border-color: #fab45a;
    box-shadow: 0 0 0 3px rgba(250, 180, 90, 0.1);
  }

  .btn-refresh {
    padding: 8px 16px;
    background: #007bff;
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s;
  }

  .btn-refresh:hover:not(:disabled) {
    background: #0056b3;
  }

  .btn-refresh:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .message {
    padding: 12px 16px;
    border-radius: 6px;
    margin-bottom: 20px;
    font-weight: 500;
  }

  .message.error {
    background-color: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  }

  .loading,
  .empty {
    text-align: center;
    padding: 60px 20px;
    color: #666;
    font-size: 16px;
  }

  .sections-list {
    display: flex;
    flex-direction: column;
    gap: 15px;
  }

  .section-card {
    background: white;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    overflow: hidden;
    transition: all 0.3s ease;
    box-shadow: 0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16);
  }

  .section-card:hover {
    border-color: #fab45a;
    box-shadow: 0px 2px 8px rgba(250, 180, 90, 0.15);
  }

  .section-card.expanded {
    border-color: #fab45a;
  }

  .section-header {
    padding: 20px;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    transition: background-color 0.2s;
  }

  .section-header:hover {
    background-color: #f8f9fa;
  }

  .section-header-content {
    flex: 1;
  }

  .section-title-group {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 10px;
    flex-wrap: wrap;
  }

  .section-topic {
    margin: 0;
    font-size: 1.3em;
    font-weight: 600;
    color: #333;
  }

  .badges {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
  }

  .badge.published {
    background-color: #d4edda;
    color: #155724;
  }

  .badge.draft {
    background-color: #fff3cd;
    color: #856404;
  }

  .badge.featured {
    background-color: #d1ecf1;
    color: #0c5460;
  }

  .section-meta {
    display: flex;
    gap: 15px;
    flex-wrap: wrap;
    font-size: 13px;
    color: #666;
  }

  .meta-item {
    display: flex;
    align-items: center;
  }

  .expand-icon {
    transition: transform 0.3s ease;
    color: #666;
    flex-shrink: 0;
    margin-left: 15px;
  }

  .expand-icon.expanded {
    transform: rotate(180deg);
  }

  .section-content {
    padding: 0 20px 20px 20px;
    border-top: 1px solid #e0e0e0;
    animation: slideDown 0.3s ease;
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      max-height: 0;
    }
    to {
      opacity: 1;
      max-height: 5000px;
    }
  }

  .content-wrapper {
    padding: 20px 0;
    line-height: 1.6;
    color: #333;
  }

  /* Style HTML content from TinyMCE */
  .content-wrapper :global(p) {
    margin-bottom: 1em;
  }

  .content-wrapper :global(h1),
  .content-wrapper :global(h2),
  .content-wrapper :global(h3),
  .content-wrapper :global(h4),
  .content-wrapper :global(h5),
  .content-wrapper :global(h6) {
    margin-top: 1.5em;
    margin-bottom: 0.5em;
    font-weight: 600;
    color: #333;
  }

  .content-wrapper :global(h1) {
    font-size: 2em;
  }

  .content-wrapper :global(h2) {
    font-size: 1.5em;
  }

  .content-wrapper :global(h3) {
    font-size: 1.25em;
  }

  .content-wrapper :global(ul),
  .content-wrapper :global(ol) {
    margin-bottom: 1em;
    padding-left: 2em;
  }

  .content-wrapper :global(li) {
    margin-bottom: 0.5em;
  }

  .content-wrapper :global(img) {
    max-width: 100%;
    height: auto;
    border-radius: 6px;
    margin: 1em 0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .content-wrapper :global(a) {
    color: #007bff;
    text-decoration: none;
  }

  .content-wrapper :global(a:hover) {
    text-decoration: underline;
  }

  .content-wrapper :global(table) {
    width: 100%;
    border-collapse: collapse;
    margin: 1em 0;
  }

  .content-wrapper :global(table td),
  .content-wrapper :global(table th) {
    padding: 8px 12px;
    border: 1px solid #ddd;
  }

  .content-wrapper :global(table th) {
    background-color: #f8f9fa;
    font-weight: 600;
  }

  .content-wrapper :global(blockquote) {
    border-left: 4px solid #fab45a;
    padding-left: 1em;
    margin: 1em 0;
    color: #666;
    font-style: italic;
  }

  .section-footer {
    margin-top: 20px;
    padding-top: 15px;
    border-top: 1px solid #e0e0e0;
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
    font-size: 12px;
    color: #666;
  }

  .footer-item {
    display: flex;
    align-items: center;
  }
</style>

